import { solarElevation, UMEA } from '@veriverse/game-logic';

/**
 * What the welcome splash needs to know about the weather outside (spec §6.11).
 *
 * This is deliberately smaller than the full normalized `Weather` model. The reading comes from
 * the room state (`weather`, filled by the server from the SMHI feed, ADR-0024); without a fresh
 * reading the condition is `unknown` (rendered as a clear sky, as the spec prescribes).
 */
export interface WelcomeWeather {
  readonly condition: WeatherCondition;
  readonly isNight: boolean;
  /** Degrees Celsius; undefined while no reading is available. */
  readonly temperatureC?: number;
}

export const WEATHER_CONDITIONS = [
  'clear',
  'partly_cloudy',
  'cloudy',
  'fog',
  'drizzle',
  'rain',
  'heavy_rain',
  'thunder',
  'sleet',
  'snow',
  'heavy_snow',
  'unknown',
] as const;

export type WeatherCondition = (typeof WEATHER_CONDITIONS)[number];

/** The animated icon drawn for a condition. */
export type WeatherIconKind =
  | 'sun'
  | 'moon'
  | 'partly-sun'
  | 'partly-moon'
  | 'cloud'
  | 'fog'
  | 'rain'
  | 'thunder'
  | 'sleet'
  | 'snow';

export function weatherIcon(
  weather: Pick<WelcomeWeather, 'condition' | 'isNight'>,
): WeatherIconKind {
  switch (weather.condition) {
    case 'clear':
    case 'unknown':
      return weather.isNight ? 'moon' : 'sun';
    case 'partly_cloudy':
      return weather.isNight ? 'partly-moon' : 'partly-sun';
    case 'cloudy':
      return 'cloud';
    case 'fog':
      return 'fog';
    case 'drizzle':
    case 'rain':
    case 'heavy_rain':
      return 'rain';
    case 'thunder':
      return 'thunder';
    case 'sleet':
      return 'sleet';
    case 'snow':
    case 'heavy_snow':
      return 'snow';
  }
}

/** Night means the sun is below the horizon at the office. */
export function isNightAt(date: Date): boolean {
  return solarElevation(date, UMEA) < 0;
}

/** The office's own time zone, so everyone sees the same day as the weather outside. */
export const OFFICE_TIME_ZONE = 'Europe/Stockholm';

/** "Saturday 10 October 2026" / "Lördag 10 oktober 2026". */
export function formatWelcomeDate(date: Date, locale: string): string {
  const parts = new Intl.DateTimeFormat(locale === 'sv' ? 'sv-SE' : 'en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: OFFICE_TIME_ZONE,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  // Both languages read weekday, day, month, year; assembling the parts ourselves keeps the
  // punctuation identical across browsers (some ICU versions add a comma after the weekday).
  const text = `${part('weekday')} ${part('day')} ${part('month')} ${part('year')}`;
  // Swedish writes weekdays in lower case; the splash shows the date on its own line.
  return text.charAt(0).toLocaleUpperCase(locale) + text.slice(1);
}

function isCondition(value: string | null): value is WeatherCondition {
  return value !== null && (WEATHER_CONDITIONS as readonly string[]).includes(value);
}

/**
 * Development override from the address bar, e.g. `?weather=snow&night=1&temp=-7`, so every
 * icon can be checked without waiting for the sky to cooperate. Returns only the fields given.
 */
export function parseWeatherOverride(search: string): Partial<WelcomeWeather> {
  const params = new URLSearchParams(search);
  const override: { -readonly [K in keyof WelcomeWeather]?: WelcomeWeather[K] } = {};
  const condition = params.get('weather');
  if (isCondition(condition)) override.condition = condition;
  const night = params.get('night');
  if (night === '1' || night === 'true') override.isNight = true;
  if (night === '0' || night === 'false') override.isNight = false;
  const temp = params.get('temp');
  if (temp !== null && temp.trim() !== '' && Number.isFinite(Number(temp))) {
    override.temperatureC = Number(temp);
  }
  return override;
}

/** The weather part of the decoded room state (mirrors the server's `WeatherState`). */
export interface WeatherReading {
  readonly condition?: string;
  readonly temperatureC?: number;
}

/**
 * Current weather for the splash: the room state's reading (temperature only when the reading is
 * real), the day/night flag from the sun position, and a development override on top.
 */
export function currentWelcomeWeather(
  now: Date,
  override: Partial<WelcomeWeather> = {},
  reading?: WeatherReading,
): WelcomeWeather {
  const condition = isCondition(reading?.condition ?? null)
    ? (reading?.condition as WeatherCondition)
    : 'unknown';
  const live: WelcomeWeather =
    condition !== 'unknown' && reading?.temperatureC !== undefined
      ? { condition, isNight: isNightAt(now), temperatureC: reading.temperatureC }
      : { condition, isNight: isNightAt(now) };
  return { ...live, ...override };
}

/** The splash always stays 5 s, then fades (spec §6.11); input never shortens it. */
export const SPLASH_VISIBLE_MS = 5_000;
export const SPLASH_FADE_MS = 800;

/** Phase of the splash `elapsedMs` after entering the office. Time is the only input. */
export function splashPhaseAt(elapsedMs: number): 'shown' | 'fading' | 'gone' {
  if (elapsedMs < SPLASH_VISIBLE_MS) return 'shown';
  if (elapsedMs < SPLASH_VISIBLE_MS + SPLASH_FADE_MS) return 'fading';
  return 'gone';
}
